import { useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const DEBUG_ENDPOINT = `${API_URL}/debug/client-error`;

// Debounce to avoid flooding the server with duplicate errors
const sentErrors = new Set<string>();
const DEDUP_WINDOW = 5000; // 5 seconds

function sendError(payload: {
  type: string;
  message: string;
  url?: string;
  source?: string;
  line?: number;
  col?: number;
  stack?: string;
}) {
  // Dedup: skip if we sent this exact error in the last 5 seconds
  const key = `${payload.type}:${payload.message}:${payload.source || ''}`;
  if (sentErrors.has(key)) return;
  sentErrors.add(key);
  setTimeout(() => sentErrors.delete(key), DEDUP_WINDOW);

  // Fire and forget — never let debug reporting break the app
  try {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', DEBUG_ENDPOINT, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.send(JSON.stringify({
      ...payload,
      url: payload.url || window.location.href,
      timestamp: new Date().toISOString()
    }));
  } catch {
    // Silently fail — debug must never crash the app
  }
}

export function ClientErrorReporter() {
  useEffect(() => {
    // 1. Capture JS runtime errors
    const prevOnError = window.onerror;
    window.onerror = (message, source, line, col, error) => {
      // Ignore errors from our own debug endpoint
      if (source?.includes('/api/debug/')) return;
      sendError({
        type: 'JS_ERROR',
        message: String(message),
        source: source || undefined,
        line: line || undefined,
        col: col || undefined,
        stack: error?.stack
      });
      if (prevOnError) prevOnError(message, source, line, col, error);
    };

    // 2. Capture unhandled promise rejections
    const prevOnRejection = window.onunhandledrejection;
    window.onunhandledrejection = (event) => {
      const reason = event.reason;
      sendError({
        type: 'UNHANDLED_PROMISE',
        message: reason?.message || String(reason),
        stack: reason?.stack
      });
      if (prevOnRejection) (prevOnRejection as any)(event);
    };

    // 3. Capture console.error calls
    const origConsoleError = console.error;
    console.error = (...args: any[]) => {
      const message = args.map(a => {
        if (a instanceof Error) return a.message;
        if (typeof a === 'object') {
          try { return JSON.stringify(a).substring(0, 300); } catch { return String(a); }
        }
        return String(a);
      }).join(' ');

      // Skip React DevTools noise and our own debug messages
      if (!message.includes('Download the React DevTools') &&
          !message.includes('[FRONTEND') &&
          !message.includes('debug/client-error')) {
        sendError({
          type: 'CONSOLE_ERROR',
          message: message.substring(0, 500)
        });
      }

      origConsoleError.apply(console, args);
    };

    // 4. Intercept fetch to catch failed API calls
    const origFetch = window.fetch;
    window.fetch = async (...args: Parameters<typeof fetch>) => {
      const response = await origFetch(...args);

      if (response.status >= 400) {
        const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request)?.url || 'unknown';
        // Don't report our own debug endpoint failures
        if (!url.includes('/api/debug/')) {
          sendError({
            type: 'FETCH_ERROR',
            message: `${response.status} ${response.statusText}`,
            source: String(response.status),
            url: window.location.href + ' → ' + url
          });
        }
      }

      return response;
    };

    // Cleanup on unmount
    return () => {
      window.onerror = prevOnError;
      window.onunhandledrejection = prevOnRejection;
      console.error = origConsoleError;
      window.fetch = origFetch;
    };
  }, []);

  // This component renders nothing — it's a pure side-effect
  return null;
}
